import React from 'react';
import { Link } from 'react-router-dom';

function FollowUpCare() {
  return (
    <article className="seo-page-container" style={{ 
      padding: 'clamp(15px, 4vw, 30px)', 
      background: '#fff', 
      margin: 'clamp(10px, 3vw, 20px)', 
      borderRadius: '4px', 
      textAlign: 'left', 
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box'
    }}>
      
      {/* HEADER HERO SECTION */}
      <header>
        <h1 style={{ 
          color: '#0b3c5d', 
          marginBottom: '15px', 
          fontSize: 'clamp(1.5rem, 6vw, 2.4rem)', 
          fontWeight: '700', 
          lineHeight: '1.3' 
        }}>
          Post-Discharge Follow-up Care Abroad: The Ultimate Patient Recovery Guide
        </h1>
        <p style={{ color: '#444', lineHeight: '1.8', fontSize: 'clamp(0.95rem, 3.5vw, 1.1rem)', marginBottom: '25px' }}>
          The success of a major surgical journey doesn’t end when you leave the operating theater or receive your hospital discharge summary. For international medical travelers navigating advanced treatments—such as complex heart bypasses, multi-joint orthopedic surgeries, or heavy oncology therapies—the post-operative healing window is vital. 
          This comprehensive research manual outlines how international patients organize **post-discharge follow-up care, medical rehabilitation, local outpatient tracking, and cross-border continuity of care** using independent directory platforms like <strong>Medical Trip World</strong>.
        </p>
      </header>

      {/* CALL TO ACTION BUTTON */}
      <div style={{ marginBottom: '35px' }}>
        <Link to="/compare-costs" style={{ 
          background: '#28a745', 
          color: '#fff', 
          padding: '14px 24px', 
          textDecoration: 'none', 
          borderRadius: '4px', 
          fontWeight: 'bold', 
          display: 'inline-block', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '0.95rem',
          textAlign: 'center',
          boxSizing: 'border-box',
          maxWidth: '100%'
        }}>
          Access Free Post-Op Recovery Checklists & Toolkits →
        </Link>
      </div>

      {/* SECTION 1: THE CRITICAL TRANSITION PHASE */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Why Structured Post-Operative Monitoring is Essential
        </h2>
        <p style={{ color: '#555', lineHeight: '1.7', marginBottom: '15px', fontSize: '0.95rem' }}>
          Transitioning from an intensive inpatient ward to a local recovery hotel or serviced apartment requires careful administrative and clinical tracking. The first 14 days following a discharge are crucial for managing wound care, adjusting pharmaceutical dosages, identifying early indicators of infection, and preventing unnecessary readmissions.
        </p>
        <p style={{ color: '#555', lineHeight: '1.7', margin: 0, fontSize: '0.95rem' }}>
          Standard global medical tourism guidance advises international patients to establish a clear synchronization strategy between their treating surgeon overseas and their primary care physician back home. This ensures that routine follow-up protocols, such as stitch removals, blood count tracking, and progressive physical therapy steps, transition smoothly without compromising long-term clinical outcomes.
        </p>
      </section>

      {/* SECTION 2: RECOVERY CHECKLIST MATRIX */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Step-by-Step Post-Discharge Coordination Checklist
        </h2>
        <p style={{ color: '#555', lineHeight: '1.6', fontSize: '0.95rem' }}>
          When organizing your medical rehabilitation and travel return windows, standard patient coordination protocols focus on the following core data segments:
        </p>

        <div style={{ background: '#f4f8fb', padding: 'clamp(15px, 4vw, 20px)', margin: '25px 0', borderRadius: '4px', borderLeft: '4px solid #005ba1', boxSizing: 'border-box' }}>
          <h3 style={{ marginTop: '0', color: '#005ba1', fontSize: '1.2rem', marginBottom: '10px' }}>📋 Post-Discharge Telemetry & Recovery Profile:</h3>
          <ul style={{ marginTop: '10px', listStyleType: 'square', paddingLeft: '20px', lineHeight: '1.8', color: '#333', fontSize: '0.95rem' }}>
            <li><strong>Comprehensive Discharge Summaries:</strong> Before leaving the clinical facility, ensure you acquire highly detailed, printed, and digital copies of surgical notes, anesthesia summaries, and laboratory diagnostics formatted in English.</li>
            <li><strong>Fit-to-Fly Travel Certification:</strong> Secure an official, signed statement from your operating specialist stating that your wound closure, blood pressure, and physical benchmarks are stable enough to tolerate long-haul flight pressure levels.</li>
            <li><strong>Local Outpatient Scheduling:</strong> Pre-book local clinic transit routes for vital intermediate follow-up diagnostics, hardware tracking, or wound redressing sessions before your departure date.</li>
            <li><strong>Extended Medication Mapping:</strong> Secure a minimum 3 to 6-month supply of specialized post-op pharmaceuticals (like anticoagulants or specialized immune management drugs) that might be difficult to source immediately in your home country.</li>
          </ul>
        </div>
      </section>

      {/* SECTION 3: SAFETY & INDEPENDENT AUDITING CHECKPOINTS - RESPONSIVE GRID */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Verifying Continuity Frameworks Independently
        </h2>
        <p style={{ color: '#555', lineHeight: '1.6', fontSize: '0.95rem' }}>
          Because Medical Trip World operates strictly as an open informational venue and independent index, we strongly advocate that traveling families verify follow-up services directly with their chosen hospital wing. Ensure your rehabilitation path integrates these quality benchmarks:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', marginTop: '15px' }}>
          <div style={{ padding: '15px', background: '#fafafa', borderRadius: '4px', border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#0b3c5d', fontSize: '1.05rem' }}>Surgeon-Led Tele-Consultations</h4>
            <p style={{ margin: '0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              Confirm that the international hospital provides scheduled, remote follow-up video portals so your home country doctor can sync with your operating surgeon regarding progress milestones.
            </p>
          </div>
          <div style={{ padding: '15px', background: '#fafafa', borderRadius: '4px', border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#0b3c5d', fontSize: '1.05rem' }}>Specialized Local Rehab Stays</h4>
            <p style={{ margin: '0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              For extensive orthopedic joint replacements, check if the local destination city offers specialized recovery accommodations featuring physical layout parameters optimized for limited-mobility patients.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4: IN-DEPTH FAQ */}
      <section style={{ marginBottom: '35px', background: '#fff', border: '1px solid #e0e0e0', padding: 'clamp(15px, 4vw, 20px)', borderRadius: '4px' }}>
        <h3 style={{ color: '#0b3c5d', marginTop: '0', marginBottom: '20px', fontSize: '1.25rem' }}>
          Frequently Asked Questions (FAQs) – Post-Op Follow-up Care
        </h3>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>What happens if a patient experiences complications after returning to their home country?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            Patients should establish a backup emergency protocol with a local specialist in their hometown before flying out. In the event of secondary complications, your local care provider can review your comprehensive overseas discharge files to initiate corrective stabilization immediately.
          </p>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>Are virtual follow-up appointments typically included in standard hospital packages?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            Many internationally certified healthcare facilities bundle 1 to 3 initial remote tele-health updates within their standard programmatic cost estimations. Patients are advised to confirm the exact scope of post-discharge virtual coverage before their final admission is completed.
          </p>
        </div>
        
        <div style={{ marginBottom: '5px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>Does Medical Trip World organize nursing staff or home care coordination?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            No. Medical Trip World functions exclusively as an independent informational directory and research portal. We do not provide medical home care, manage nursing networks, or maintain formal corporate partnerships or Memorandums of Understanding (MOUs) with any hospital system or rehabilitation facility. All clinical screening, financial agreements, and follow-up care arrangements take place directly and exclusively between the patient and their self-selected healthcare professional.
          </p>
        </div>
      </section>

      {/* RE-ENGAGEMENT FOOTER */}
      <footer style={{ borderTop: '1px solid #eee', paddingTop: '20px', textAlign: 'center' }}>
        <p style={{ fontSize: '1.05rem', fontWeight: 'bold', color: '#333', margin: '0 0 15px 0' }}>Empower Your Global Healthcare Research Journey Safely</p>
        <div style={{ display: 'flex', gap: '12px', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
          <style>{`
            @media (max-width: 550px) {
              .footer-btn { width: 100% !important; text-align: center; box-sizing: border-box; }
            }
          `}</style>
          <Link to="/submit-query" className="footer-btn" style={{ background: '#28a745', color: '#fff', padding: '12px 20px', textDecoration: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', display: 'inline-block' }}>
            Submit General Information Request
          </Link>
          <Link to="/compare-costs" className="footer-btn" style={{ background: '#fff', color: '#0b3c5d', border: '2px solid #0b3c5d', padding: '10px 20px', textDecoration: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', display: 'inline-block' }}>
            View Estimated Cost Charts
          </Link>
        </div>
      </footer>

    </article>
  );
}

export default FollowUpCare;